<template>
  <button
    type="button"
    :class="{
      'leading-none inline-block transition-all relative top-[2px] hover:text-primary-500': true,
      active: isActive,
    }"
    id="settingsMenuDropdown"
    @click.stop="toggleActive"
  >
    <i class="material-symbols-outlined !text-[22px] md:!text-[24px]">
      settings
    </i>
  </button>

  <div
    v-if="isActive"
    class="settings-menu-dropdown bg-white dark:bg-[#0c1427] transition-all shadow-3xl dark:shadow-none p-[20px] absolute mt-[17px] md:mt-[20px] w-[195px] z-[1] top-full ltr:right-0 rtl:left-0 rounded-md"
  >
    <button
      type="button"
      class="rtl-mode-toggle flex items-center text-black dark:text-white font-medium"
      @click="toggleDirection"
    >
      {{ rtlModeText }}:
      <span
        class="inline-block relative rounded-full w-[35px] h-[20px] bg-gray-50 dark:bg-[#0a0e19] ltr:ml-[10px] rtl:mr-[10px]"
      >
        <span
          class="inline-block transition-all absolute h-[12px] w-[12px] bg-black dark:bg-white rounded-full top-1/2 -translate-y-1/2"
          :style="{
            left: direction === 'ltr' ? '4px' : 'calc(100% - 16px)',
          }"
        ></span>
      </span>
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted, watch, computed } from "vue";
import { useI18n } from "vue-i18n";
import { setLanguage, type SupportedLanguage } from "@/i18n";

export default defineComponent({
  name: "SettingsBtn",
  setup() {
    const { locale, t } = useI18n();
    const isActive = ref(false);
    const direction = ref(localStorage.getItem("direction") || "ltr"); // Load from local storage
    
    // Direct translation function
    const directT = (key: string): string => {
      // Make sure we actually have a translation and not just returning the key
      const translation = t(key);
      if (!translation || translation === key) {
        console.warn(`Missing translation for key: ${key}`);
      }
      return translation || key;
    };
    
    // Computed property for RTL mode text to ensure reactivity
    const rtlModeText = computed(() => {
      const currentLang = locale.value as string;
      if (currentLang === 'en') {
        return "Right-to-Left Mode";
      } else if (currentLang === 'he') {
        return "מצב ימין לשמאל";
      } else if (currentLang === 'ar') {
        return "وضع اليمين إلى اليسار";
      }
      return directT('common.rtlMode'); // Fallback
    });

    const toggleActive = () => {
      isActive.value = !isActive.value;
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const button = document.getElementById("settingsMenuDropdown");
      if (button && !button.contains(target)) {
        isActive.value = false;
      }
    };

    const toggleDirection = () => {
      direction.value = direction.value === "ltr" ? "rtl" : "ltr";
      document.body.setAttribute("dir", direction.value);
      localStorage.setItem("direction", direction.value); // Persist to local storage
      
      // Set language based on direction if appropriate
      if (direction.value === 'rtl') {
        // Default to Hebrew for RTL if current language isn't already Arabic
        if (locale.value !== 'ar') {
          setLanguage('he');
        }
      } else {
        // Default to English for LTR if current language is Hebrew or Arabic
        if (['he', 'ar'].includes(locale.value as string)) {
          setLanguage('en');
        }
      }
      
      // Close the dropdown after making a selection
      isActive.value = false;
    };

    // Watch for language changes to sync direction
    watch(() => locale.value, (newLocale) => {
      const isRtl = ['he', 'ar'].includes(newLocale as string);
      const newDirection = isRtl ? 'rtl' : 'ltr';
      if (direction.value !== newDirection) {
        direction.value = newDirection;
        document.body.setAttribute('dir', newDirection);
        localStorage.setItem('direction', newDirection);
      }
    });

    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
      
      // Ensure direction matches language on mount
      const isRtl = ['he', 'ar'].includes(locale.value as string);
      const newDirection = isRtl ? 'rtl' : 'ltr';
      
      if (direction.value !== newDirection) {
        direction.value = newDirection;
        document.body.setAttribute('dir', newDirection);
        localStorage.setItem('direction', newDirection);
      }
      
      // Force a refresh of translations on mount to ensure they're loaded
      console.log('Mounted, current RTL mode translation:', directT('common.rtlMode'));
    });

    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    return {
      isActive,
      toggleActive,
      direction,
      toggleDirection,
      directT,
      rtlModeText
    };
  },
});
</script>
